import { useApiClientStore } from '../stores/useApiClientStore';
import { ApiResult } from '../types';
import type { MessageThread } from '../types/message';
import {
  MESSAGE_REPLY_PATH,
  MESSAGE_THREAD_PATH,
  MESSAGE_VERIFY_PATH,
} from '../data/fixtures/messages.data';

type Body = { ok: true } | { ok: false; errors?: unknown };

const withMessageId = (path: string, messageId: string) =>
  path.replace(':messageId', encodeURIComponent(messageId));

/**
 * The shipped endpoint (ENG-21822) does not match the shape Plan - Comms integration
 * agreed: it sends `patientMessageId`/`patientGivenName`, a pre-formatted
 * `createdDateTime`, `direction` as an object, the body as HTML under `message`, and
 * no per-message id. Normalise here so the views hold one shape and keep working
 * whichever way the contract is settled.
 */
export function normaliseThread(raw: any, fallbackId: string): MessageThread {
  const messages = (raw?.messages ?? []).map((message: any, index: number) => ({
    id: message.id ?? `message-${index}`,
    direction: normaliseDirection(message.direction),
    title: message.title ?? null,
    createdDateTime: message.createdDateTime,
    isStructuredCommunication: Boolean(message.isStructuredCommunication),
    // `message` is server-sanitised HTML; `body` is the plain-text form the fixtures use.
    bodyHtml: typeof message.message === 'string' ? message.message : undefined,
    body: message.body,
    responseDetails: message.responseDetails ?? [],
    attachments: message.attachments ?? [],
  }));

  return {
    messageId: raw?.messageId ?? raw?.patientMessageId ?? fallbackId,
    firstName: raw?.firstName ?? raw?.patientGivenName ?? null,
    canReply: Boolean(raw?.canReply),
    replyWarning: raw?.replyWarning ?? null,
    messages,
  };
}

/**
 * The API reports direction from the **clinician's** side: a message the practice sent is
 * `outbound`, and the patient's own reply is `inbound`. The portal is patient-facing, so
 * both are flipped here — otherwise the practice's message reads "You sent" and the
 * patient's own reply reads "Sent".
 */
function normaliseDirection(direction: unknown): 'INBOUND' | 'OUTBOUND' {
  return fromClinicianPerspective(direction) === 'OUTBOUND' ? 'INBOUND' : 'OUTBOUND';
}

function fromClinicianPerspective(direction: unknown): 'INBOUND' | 'OUTBOUND' {
  if (typeof direction === 'string') {
    return direction.toUpperCase() === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND';
  }

  const value = (direction as { value?: string; isOutbound?: boolean }) ?? {};
  if (value.isOutbound === true) return 'OUTBOUND';
  if (value.value) return value.value.toUpperCase() === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND';

  return 'INBOUND';
}

// ENG-21791 asks whether this session may read the thread; skipErrorHandler so the caller,
// not the global handler, decides where to go
export type ResolvedThread =
  | { status: 'ok'; thread: MessageThread }
  | { status: 'unverified' }
  | { status: 'error'; code?: string };

export async function resolveMessageThreadApi(messageId: string): Promise<ResolvedThread> {
  const api = useApiClientStore().getClient();

  const res = await api.getRaw<unknown>(withMessageId(MESSAGE_THREAD_PATH, messageId), {
    skipErrorHandler: true,
  });

  if (res.ok) return { status: 'ok', thread: normaliseThread(res.data, messageId) };

  const code = Array.isArray(res.errors) ? (res.errors[0] as { code?: string })?.code : undefined;

  if (res.status === 401) return { status: 'unverified' };

  return { status: 'error', code };
}

export async function loadMessageThreadApi(messageId: string): Promise<MessageThread> {
  const api = useApiClientStore().getClient();

  const raw = await api.get<unknown>(withMessageId(MESSAGE_THREAD_PATH, messageId));

  return normaliseThread(raw, messageId);
}

export async function verifyDateOfBirthApi(
  messageId: string,
  dateOfBirth: string,
): Promise<ApiResult> {
  const api = useApiClientStore().getClient();

  const res = await api.postRaw<Body>(withMessageId(MESSAGE_VERIFY_PATH, messageId), {
    dateOfBirth,
  });

  return res.ok ? { ok: true } : { ok: false, errors: res.errors };
}

/**
 * The endpoint validates `reply` (not `replyMessage`) and builds UploadedFiles from
 * `attachments`, so this is always multipart — see PatientMessageController.php:123.
 */
export async function submitReplyApi(
  messageId: string,
  replyMessage: string,
  attachments: File[] = [],
): Promise<ApiResult> {
  const api = useApiClientStore().getClient();

  const fd = new FormData();
  fd.append('reply', replyMessage);
  for (const file of attachments) {
    fd.append('attachments[]', file, file.name);
  }

  const res = await api.postRawFiles<Body>(withMessageId(MESSAGE_REPLY_PATH, messageId), fd);

  return res.ok ? { ok: true } : { ok: false, errors: res.errors };
}
